import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { injectHotkeyManagerService } from '@libs/shared/hotkey/hotkey-manager.service';
import { DeleteWithTypeDialogConfig } from '@libs/shared/ui/dialogs/delete-with-type-dialog/delete-with-type-dialog.type';
import { allAllowInElements } from '@libs/shared/utils/const/shortcut';

@Component({
  selector: 'app-delete-with-type-dialog',
  template: `
    <h1 mat-dialog-title class="text-xl font-bold">
      {{ data.title }}
    </h1>
    <div mat-dialog-content class="flex flex-col gap-4">
      <div
        class="whitespace-pre-line rounded-2xl bg-red-100 px-4 py-2 dark:bg-red-900">
        <span class="text-red-700 dark:text-red-300" [innerHtml]="data.message">
        </span>
      </div>
      <div
        class="text-3xl font-bold text-red-500"
        [innerHtml]="data.text"></div>
      <form [formGroup]="formGroup" (keydown.enter)="$event.preventDefault()">
        <div class="flex flex-col gap-2">
          <mat-form-field
            [floatLabel]="'always'"
            class="fuse-mat-no-header w-full"
            appearance="fill">
            <input
              matInput
              formControlName="text"
              [placeholder]="data.placeholder" />
          </mat-form-field>
        </div>
      </form>
    </div>
    <div mat-dialog-actions class="flex flex-row justify-end">
      <button mat-stroked-button (click)="onClose()">돌아가기</button>
      <button
        mat-flat-button
        color="warn"
        [disabled]="formGroup.invalid"
        (click)="onSubmit()">
        {{ data.submitText }}
      </button>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteWithTypeDialogComponent implements AfterViewInit, OnDestroy {
  readonly fb = inject(FormBuilder);
  // prettier-ignore
  readonly hotkeyManager = injectHotkeyManagerService();

  formGroup = this.fb.group({
    text: [null, [Validators.required, this.matchTextValidator.bind(this)]],
  }) as DeleteWithTypeFormGroup;
  constructor(
    // prettier-ignore
    public matDialogRef: MatDialogRef<DeleteWithTypeDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: DeleteWithTypeDialogConfig
  ) {}

  ngAfterViewInit(): void {
    this.hotkeyManager.activateHotkeysForComponent(this.constructor.name, [
      {
        keys: 'meta.enter',
        description: '저장하기',
        allowIn: allAllowInElements,
        action: () => {
          this.onSubmit();
        },
      },
      {
        keys: 'escape',
        description: '닫기',
        allowIn: allAllowInElements,
        action: () => {
          this.onClose();
        },
      },
    ]);
  }

  private matchTextValidator(
    control: FormControl
  ): { [key: string]: any } | null {
    return control.value === this.data.text ? null : { textMismatch: true };
  }

  ngOnDestroy(): void {
    this.hotkeyManager.deactivateHotkeys(this.constructor.name);
  }

  onSubmit() {
    if (this.formGroup.valid) {
      this.matDialogRef.close(true);
    }
  }

  onClose() {
    this.matDialogRef.close(false);
  }
}

export type DeleteWithTypeFormGroup = FormGroup<{
  text: FormControl<string | null>;
}>;
